import { CAMADAS, type CamadaId } from './camadas.js';
import type { Candidato } from './candidato.js';
import { enderecoPorExtenso } from './endereco.js';
import { impressaoDosDados, type DadosImpressos, type TipoDocumento } from './documentos.js';
import { ocupaCadeira, type EtapaId } from './etapas.js';

/**
 * Regras que amarram a ficha aos instrumentos contratuais e à lotação da comunidade.
 * Vivem aqui, e não na rota, para o cockpit poder antecipar a recusa em vez de descobrir
 * no erro do servidor.
 */

/** Monta os dados impressos a partir da ficha, para comparar com o documento emitido. */
export function dadosImpressosDe(
  candidato: Candidato,
  valores: { total: number; sinal: number; saldo: number },
): DadosImpressos {
  return {
    tipo: candidato.tipo,
    razao: candidato.razao,
    doc: candidato.doc,
    endereco: enderecoPorExtenso(candidato.endereco),
    telefone: candidato.telefone,
    representante: candidato.representante,
    email: candidato.email,
    camada: candidato.camada,
    numero: candidato.numero,
    referencia: candidato.referencia,
    valores: `${valores.total}|${valores.sinal}|${valores.saldo}`,
    titulares: candidato.titulares
      .map((t) => `${t.nome}~${t.cargo}~${t.cpf}~${t.contato}`)
      .join(';'),
    curadoriaDecidida: candidato.marcos.curadoriaDecidida ?? '',
  };
}

export function impressaoAtual(
  candidato: Candidato,
  valores: { total: number; sinal: number; saldo: number },
  tipo: TipoDocumento,
): string {
  return impressaoDosDados(tipo, dadosImpressosDe(candidato, valores));
}

/**
 * Lotação da camada. O Regimento, art. 2º, fixa 100 cadeiras distribuídas em
 * 55 / 30 / 15 — vender além disso é problema contratual, não erro de tela.
 */
export interface OcupacaoDaCamada {
  camada: CamadaId;
  nome: string;
  ocupadas: number;
  total: number;
  restantes: number;
  esgotada: boolean;
}

/** Quem está com uma cadeira específica. */
export interface CadeiraTomada {
  numero: string;
  camada: CamadaId;
  candidatoId: string;
  /** Razão social ou nome, para o operador saber a quem pedir a liberação. */
  razao: string;
  etapa: EtapaId;
}

/**
 * A cadeira de cada ficha, indexada pelo número.
 *
 * **Não filtra por etapa de propósito.** A reserva no banco nasce do número gravado na
 * ficha, seja qual for a etapa — inclusive `recusado` e `desistiu`, que seguram o número
 * até alguém apagá-lo ou excluir a ficha. Filtrar aqui ofereceria como livre uma cadeira
 * que o servidor vai recusar na hora de salvar.
 *
 * A chave é só o número porque a numeração é corrida de 001 a 100: cada número pertence a
 * uma única camada (ver `FAIXAS`).
 */
export function cadeirasTomadas(
  candidatos: readonly Pick<Candidato, 'camada' | 'numero' | 'candidatoId' | 'razao' | 'etapa'>[],
): Map<string, CadeiraTomada> {
  const mapa = new Map<string, CadeiraTomada>();
  for (const c of candidatos) {
    if (!c.numero) continue;
    mapa.set(c.numero, {
      numero: c.numero,
      camada: c.camada,
      candidatoId: c.candidatoId,
      razao: c.razao,
      etapa: c.etapa,
    });
  }
  return mapa;
}

export function ocupacaoDaCamada(
  camada: CamadaId,
  candidatos: readonly Pick<Candidato, 'camada' | 'etapa' | 'candidatoId'>[],
  ignorarCandidatoId?: string,
): OcupacaoDaCamada {
  const ocupadas = candidatos.filter(
    (c) => c.camada === camada && ocupaCadeira(c.etapa) && c.candidatoId !== ignorarCandidatoId,
  ).length;

  const total = CAMADAS[camada].cadeiras;

  return {
    camada,
    nome: CAMADAS[camada].nome,
    ocupadas,
    total,
    restantes: Math.max(0, total - ocupadas),
    esgotada: ocupadas >= total,
  };
}
